import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import i18n from "i18next";
import {
  LuLoaderCircle,
  LuRefreshCw,
  LuZoomIn,
  LuFilm,
  LuClapperboard,
  LuCircleX,
  LuX,
  LuVideo,
  LuImage,
  LuCheck,
  LuSettings2,
  LuChevronLeft,
  LuPlay,
  LuPause,
  LuRotateCcw,
  LuPlus,
} from "react-icons/lu";

// Shared with ImageAnimatorModal — moved to merlinStyles.js so both files
// import one source instead of duplicating the const or creating a cycle.
import { belowImageBtnStyle } from "./merlinStyles";
import { credentialsFor } from "../../../common/utils";
import "./_animationEditMode.scss";

const railHeaderStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 6,
};

// Phase-1 owner directive: total (parent + all extends) duration is capped at
// 45s. EXTEND_CHUNK_SEC mirrors MW's EXTEND_CHUNK_DURATION_SEC (animate-v2
// chunks are always 8s) — kept local since this file has no MW import.
const CAP_SEC = 45;
const EXTEND_CHUNK_SEC = 8;

// S256 redesign: Süre / Biçim / Devam görseli tabs replace the old un-tabbed
// 280px rail (three unrelated control groups jammed into one scrolling
// column). Labels resolved inline (not hoisted) since i18n.t needs the
// component's already-initialized instance.
const TAB_IDS = ["duration", "format", "continuation"];

export default function AnimationEditMode({
  version,
  elementWindow,
  elementWindowSec,
  isBusy,
  jobProgress,
  jobFailedMessage,
  advancedOpen,
  setAdvancedOpen,
  hideAdvanced = false, // the "Gelişmiş" toggle; ImageAnimatorModal hides it (mp4 results edit in the Video Director)
  extendPrompt,
  onExtendPromptChange,
  extendProvider,
  onExtendProviderChange,
  onExtend,
  onConvert,
  onAddAsMp4,
  mp4Applied,
  isVideoSourceBox,
  onAddAsWebp,
  onOpenLightbox,
  onDismissError,
  replayTs,
  onReplay,
  referenceImage,
  onPickReferenceLibrary,
  onPickReferenceScene,
  onReferenceUpload,
  onClearReference,
  referenceUploading,
  referenceError,
  onUseFrameAsReference,
  frameGrabbing,
  embed = false,
}) {
  const isMp4 = /\.mp4(\?|#|$)/i.test(version.displayUrl || "");
  // showPlayerBar: play-once versions with a source mp4 show that mp4 AS the
  // big preview (the bar below drives it) instead of the webp/gif img. Loop
  // versions and versions with no mp4Url keep the plain img preview, no bar.
  const showPlayerBar = !!version.mp4Url && !version.loop;
  const displayUrl =
    !showPlayerBar && replayTs
      ? `${version.displayUrl}${version.displayUrl.includes("?") ? "&" : "?"}__t=${replayTs}`
      : version.displayUrl;
  const canDerive = !!version.mp4Url;
  const isGemini = ["gemini", "gemini-fast"].includes(version.provider);
  const animSec = version.durationSec;
  const hasWindow = elementWindowSec != null && typeof animSec === "number";
  // fitTargetSec: the TOTAL duration "Sahne süresine uydur" aims for, clamped
  // to both the box's own scene window AND the 45s cap. capReached: no room
  // for even one more 8s chunk — hides/disables "Uzat (+8 sn)" outright
  // regardless of elementWindowSec (that button has no target, so it can only
  // be gated by the cap itself).
  const capReached = typeof animSec === "number" && animSec + EXTEND_CHUNK_SEC > CAP_SEC;
  const fitTargetSec = hasWindow ? Math.min(elementWindowSec, CAP_SEC) : null;
  const isShort = fitTargetSec != null && animSec < fitTargetSec;
  const reachableSec = isShort
    ? Math.min(
        CAP_SEC,
        animSec + EXTEND_CHUNK_SEC * Math.ceil((fitTargetSec - animSec) / EXTEND_CHUNK_SEC)
      )
    : animSec;
  const deficitSec = hasWindow ? Math.max(0, elementWindowSec - animSec) : 0;
  const canFit = canDerive && isGemini && isShort && !capReached;

  // Advanced-view pill buttons (mockup: Ayarla/Duzenle/Main/GelismisDevam.html)
  // — one flat neutral pill for secondary actions, one indigo-tinted pill for
  // the primary "fit to scene" action. Replaces the old 280px-rail's
  // actionBtnStyle/gridBtnStyle look now that Süre/Biçim/Devam görseli render
  // as full-width tab content instead of a side rail.
  const tabPillBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#475569",
    fontSize: 13,
    fontWeight: 600,
    cursor: isBusy ? "not-allowed" : "pointer",
    opacity: isBusy ? 0.6 : 1,
    whiteSpace: "nowrap",
  };
  const tabPillBtnPrimaryStyle = {
    ...tabPillBtnStyle,
    border: "1px solid #c7d2fe",
    background: "#eef2ff",
    color: "#4f46e5",
    fontWeight: 700,
  };
  const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#475569",
    fontSize: 11,
    fontWeight: 600,
  };

  // Prompt textarea hidden by default (owner directive): a subtle "Prompt
  // ekle" toggle expands it in place. Non-empty text keeps it expanded (the
  // value lives in the PARENT as extendPrompt, so it survives this
  // component's picker-roundtrip unmounts even though promptOpen is local).
  const [promptOpen, setPromptOpen] = useState(false);
  const promptExpanded = promptOpen || !!extendPrompt;

  // Tab within the advanced view (Süre / Biçim / Devam görseli — replaces the
  // old un-tabbed rail). Defaults to the continuation tab when a reference
  // image is already set at mount time: covers both opening a version that
  // already has one AND remounting after the Sahneden/Kütüphaneden picker
  // roundtrip (this component unmounts during that roundtrip — see the
  // Simple/advanced split comment below), so the user lands back on the tab
  // their pick just landed in instead of always on Süre.
  const [activeTab, setActiveTab] = useState(() => (referenceImage ? "continuation" : "duration"));

  // Simple/advanced split (owner directive): Mode B opens in a SIMPLE view —
  // preview + one info line + the single fit-to-scene primary action. The full
  // tabbed panel (Süre/Biçim/Devam görseli) and the frame-scrub row only
  // render behind the "Gelişmiş" toggle. advancedOpen is owned by the
  // PARENT (ImageAnimatorModal) and passed down as a controlled prop — this
  // component unmounts during the Sahneden/Kütüphaneden picker roundtrip, so
  // local state would have reset to false on remount; the parent only resets
  // it to false on Mode B entry/exit, never on a picker open/close.
  const infoParts = [];
  if (elementWindow) {
    infoParts.push(
      i18n.t("AnimEdit.InfoShownWindow", {
        start: elementWindow.startSec.toFixed(1),
        end: elementWindow.endSec.toFixed(1),
      })
    );
  }
  if (elementWindowSec != null) {
    infoParts.push(i18n.t("AnimEdit.InfoNeeded", { sec: elementWindowSec.toFixed(1) }));
  }
  if (typeof animSec === "number") {
    infoParts.push(i18n.t("AnimEdit.SummaryDuration", { sec: animSec.toFixed(1) }));
  }

  // Player bar (below the big preview) — mounted whenever showPlayerBar is
  // true, in both simple and advanced view. The <video> IS the big preview
  // (see render below); the bar only supplies play/pause/seek/replay chrome
  // for it. Simple view plays version.mp4Url directly (no CORS issue, no
  // fetch). Advanced view additionally fetches the mp4 as a blob (below) so
  // the frame-capture button can read canvas pixels without CORS tainting;
  // once the blob is ready the <video>'s src swaps to it, and playerTime/
  // isPlaying (restored via onLoadedMetadata) carry the swap across so
  // playback doesn't visibly jump. Playback state resets on version switch
  // so a stale duration/time from the previous mp4 doesn't linger.
  const playerRef = useRef(null);
  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => {
    setPlayerTime(0);
    setPlayerDuration(0);
    setIsPlaying(false);
  }, [version.mp4Url]);

  // Same-origin blob trick: fetch the mp4 once per advanced-open and play the
  // <video> from a blob: URL instead of the (cross-origin CDN) mp4Url —
  // canvas capture of a blob: URL video is never CORS-tainted, so "use this
  // frame" can grab pixels client-side instead of round-tripping to the
  // server's ffmpeg-backed /frame endpoint (which the main app doesn't have
  // ffmpeg for). scrubFallback is the single switch: if the fetch fails
  // (network/CORS), the <video> falls back to playing mp4Url directly
  // (playback needs no CORS) and the capture button falls back to that
  // server endpoint.
  const [scrubBlobUrl, setScrubBlobUrl] = useState(null);
  const [scrubLoading, setScrubLoading] = useState(false);
  const [scrubFallback, setScrubFallback] = useState(false);
  useEffect(() => {
    setScrubBlobUrl(null);
    setScrubFallback(false);
    // Blob is only needed for canvas capture in advanced view, which only
    // exists while the bar (and its <video>) is mounted at all.
    if (!showPlayerBar || !advancedOpen) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setScrubLoading(true);
    (async () => {
      try {
        const res = await fetch(version.mp4Url, credentialsFor(version.mp4Url));
        if (!res.ok) throw new Error("frame source fetch failed");
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setScrubBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setScrubFallback(true);
      } finally {
        if (!cancelled) setScrubLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [showPlayerBar, advancedOpen]);

  const playerSrc = advancedOpen && scrubBlobUrl && !scrubFallback ? scrubBlobUrl : version.mp4Url;

  // jsdom's <video>.play() is a no-op stub (no rejected Promise); optional
  // chaining keeps this a one-liner in both real browsers and tests.
  const safePlay = (video) => video?.play()?.catch(() => {});

  // isBusy: pause and drop play state — a derivative job is in flight.
  useEffect(() => {
    if (isBusy) {
      playerRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isBusy]);

  const handlePlayerLoadedMetadata = (e) => {
    const video = e.currentTarget;
    setPlayerDuration(video.duration || 0);
    if (playerTime > 0) video.currentTime = playerTime;
    if (isPlaying) safePlay(video);
  };

  const togglePlayerPlay = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pause();
      setIsPlaying(false);
    } else {
      safePlay(playerRef.current);
      setIsPlaying(true);
    }
  };

  const replayPlayer = () => {
    if (!playerRef.current) return;
    playerRef.current.currentTime = 0;
    setPlayerTime(0);
    safePlay(playerRef.current);
    setIsPlaying(true);
  };

  const handleUseFrame = () => {
    if (scrubFallback) {
      onUseFrameAsReference(playerTime);
      return;
    }
    const video = playerRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob)
        onReferenceUpload(new File([blob], `frame-${Date.now()}ms.png`, { type: "image/png" }));
    }, "image/png");
  };

  // Failure banner — rendered in the simple block by default, at the top of
  // the tabbed panel when advanced is open (exactly one of the two mounts at
  // a time).
  const errorBanner = jobFailedMessage && (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 6,
        padding: "8px 10px",
        borderRadius: 8,
        border: "1.5px solid #fca5a5",
        background: "#fff5f5",
        color: "#ef4444",
        fontSize: 12,
      }}
    >
      <LuCircleX size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span style={{ flex: 1 }}>{jobFailedMessage}</span>
      <button
        type="button"
        onClick={onDismissError}
        style={{
          border: "none",
          background: "none",
          color: "#ef4444",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <LuX size={13} />
      </button>
    </div>
  );

  const tabLabels = {
    duration: i18n.t("AnimEdit.TabDuration"),
    format: i18n.t("AnimEdit.TabFormat"),
    continuation: i18n.t("AnimEdit.TabContinuation"),
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginBottom: 14,
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Preview — flex:"1 1 auto" in the SIMPLE view, it SHRINKS to whatever
          vertical space is left. Every other row (player bar, capture
          button, replay/preview, the scene-fit card, the simple/advanced
          block below) is flex:"none" so it always keeps its natural height
          and is never clipped or scrolled away, at any viewport height.
          Advanced view fixes the preview at a flat 240px instead (flex:
          "0 0 auto") — matching the mockup (GelismisDevam.dc.html) — rather
          than capping growth with vh-relative max-height: on a short
          viewport "26vh" could shrink below what the image needs to render
          uncropped-looking at its own aspect ratio, and it still competed
          with flex-grow for the final size. A flat height removes both: the
          box is always exactly 240px regardless of viewport, and the image
          (object-fit: contain, never cover) always scales down inside it
          centered, with no cropping. */}
      <div
        style={{
          minHeight: 0,
          boxSizing: "border-box",
          ...(advancedOpen ? { flex: "0 0 auto", height: 240 } : { flex: "1 1 auto" }),
          border: "2px dashed #e2e8f0",
          borderRadius: 10,
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          padding: 6,
          position: "relative",
        }}
      >
        {showPlayerBar ? (
          <video
            ref={playerRef}
            src={playerSrc}
            muted
            playsInline
            onLoadedMetadata={handlePlayerLoadedMetadata}
            onTimeUpdate={(e) => setPlayerTime(e.currentTarget.currentTime)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        ) : (
          <img
            src={displayUrl}
            alt="Animation"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: 6,
            }}
          />
        )}
        {version.badge && (
          <span
            style={{
              position: "absolute",
              top: 6,
              left: 6,
              minWidth: 18,
              padding: "1px 5px",
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 700,
              color: "#fff",
              background: "rgba(15,23,42,0.72)",
            }}
          >
            {version.badge}
          </span>
        )}
        <span
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            color: version.loop ? "#007bff" : "#64748b",
            background: version.loop ? "#eef5ff" : "#f8fafc",
            border: `1px solid ${version.loop ? "#bfdbfe" : "#e2e8f0"}`,
          }}
        >
          {i18n.t(version.loop ? "Loop" : "Play Once")}
        </span>
        {isBusy && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(248,250,252,0.85)",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <LuLoaderCircle
              size={26}
              color="#3b82f6"
              style={{ animation: "spin 1s linear infinite" }}
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
              {i18n.t("Processing")}…
            </span>
            {jobProgress > 0 && (
              <span style={{ fontSize: 11, color: "#94a3b8" }}>{jobProgress}%</span>
            )}
          </div>
        )}
      </div>

      {/* Player bar — sits directly below the big preview and drives the
          SAME <video> rendered there (playerRef). Only mounted when the
          big preview is showing that video (showPlayerBar); loop versions
          and img-only versions never get a bar. */}
      {showPlayerBar && (
        <div
          style={{
            flex: "none",
            padding: "8px 10px",
            border: "1px solid #e9ecef",
            borderRadius: 8,
            background: "#fafbfc",
          }}
        >
          {scrubLoading && (
            <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", marginBottom: 6 }}>
              {i18n.t("Loading")}…
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={togglePlayerPlay}
              style={belowImageBtnStyle}
              disabled={isBusy}
              aria-label={i18n.t(isPlaying ? "Pause" : "Play")}
            >
              {isPlaying ? <LuPause size={12} /> : <LuPlay size={12} />}
            </button>
            <input
              type="range"
              min={0}
              max={playerDuration || 0}
              step={0.1}
              value={playerTime}
              disabled={isBusy || !playerDuration}
              onChange={(e) => {
                const t = parseFloat(e.target.value);
                setPlayerTime(t);
                if (playerRef.current) playerRef.current.currentTime = t;
              }}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: 10, color: "#94a3b8", minWidth: 64, textAlign: "right" }}>
              {i18n.t("AnimEdit.FrameTimeReadout", {
                t: playerTime.toFixed(1),
                d: playerDuration.toFixed(1),
              })}
            </span>
            <button
              type="button"
              onClick={replayPlayer}
              style={belowImageBtnStyle}
              disabled={isBusy}
              aria-label={i18n.t("Replay")}
            >
              <LuRotateCcw size={12} />
            </button>
          </div>
        </div>
      )}

      <div style={{ flex: "none", display: "flex", justifyContent: "center", gap: 6 }}>
        {!showPlayerBar && (
          <button type="button" onClick={onReplay} style={belowImageBtnStyle} disabled={isBusy}>
            <LuRefreshCw size={12} /> {i18n.t("Replay")}
          </button>
        )}
        <button
          type="button"
          onClick={() => onOpenLightbox(version.displayUrl)}
          style={belowImageBtnStyle}
        >
          <LuZoomIn size={12} /> {i18n.t("Preview")}
        </button>
      </div>

      {/* Scene-fit readout — a progress bar (S256 redesign) instead of the
          old dot-delimited text line. Falls back to that plain text line
          when there's no scene window to compare against (hasWindow false)
          but SOME timing info is still known (e.g. just the animation's own
          duration). */}
      {hasWindow ? (
        <div
          style={{
            flex: "none",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            background: "#f8fafc",
            padding: "11px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
              {elementWindow
                ? i18n.t("AnimEdit.InfoShownWindow", {
                    start: elementWindow.startSec.toFixed(1),
                    end: elementWindow.endSec.toFixed(1),
                  })
                : ""}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
              {i18n.t("AnimEdit.SceneFitRequired", { sec: elementWindowSec.toFixed(1) })}
            </span>
          </div>
          <div
            style={{
              position: "relative",
              height: 8,
              borderRadius: 999,
              background: "#e2e8f0",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                position: "absolute",
                inset: 0,
                right: `${100 - Math.min(100, (animSec / elementWindowSec) * 100)}%`,
                background: "#7c3aed",
                borderRadius: 999,
              }}
            />
            {isShort && (
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${Math.min(100, (animSec / elementWindowSec) * 100)}%`,
                  right: 0,
                  background:
                    "repeating-linear-gradient(115deg, #c7d2fe 0 4px, transparent 4px 8px)",
                }}
              />
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
              <span style={{ color: "#7c3aed" }}>
                {i18n.t("AnimEdit.SceneFitProduced", { sec: animSec.toFixed(1) })}
              </span>
              {isShort &&
                ` · ${i18n.t("AnimEdit.SceneFitShortfall", { sec: deficitSec.toFixed(1) })}`}
            </span>
            {advancedOpen && (
              <span
                style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap" }}
              >
                {i18n.t("AnimEdit.SceneFitCapNote", { sec: CAP_SEC })}
              </span>
            )}
          </div>
        </div>
      ) : (
        infoParts.length > 0 && (
          <div style={{ flex: "none", fontSize: 12, color: "#64748b", textAlign: "center" }}>
            {infoParts.join(" · ")}
          </div>
        )
      )}

      {/* SIMPLE view (default): single fit-to-scene primary action +
          "Gelişmiş" toggle, side by side. Everything else lives behind the
          toggle — nothing is removed, only deferred. */}
      {!advancedOpen && (
        <div style={{ flex: "none", display: "flex", flexDirection: "column", gap: 8 }}>
          {errorBanner}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {canFit && (
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onExtend("to-target", fitTargetSec - animSec)}
                style={tabPillBtnPrimaryStyle}
              >
                <LuClapperboard size={13} />{" "}
                {i18n.t("ExtendToSceneDuration", { sec: Math.ceil(reachableSec) })}
              </button>
            )}
            <span style={{ flex: 1 }} />
            {!hideAdvanced && (
              <button
                type="button"
                disabled={isBusy}
                onClick={() => setAdvancedOpen(true)}
                style={tabPillBtnStyle}
              >
                <LuSettings2 size={12} /> {i18n.t("AnimEdit.Advanced")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ADVANCED view: Süre / Biçim / Devam görseli tabs replace the old
          280px un-tabbed rail (S256 redesign — three unrelated control
          groups no longer share one scrolling column). */}
      {advancedOpen && (
        <div
          style={{
            flex: "none",
            border: "1px solid #e9ecef",
            borderRadius: 10,
            background: "#ffffff",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: 42,
              padding: "0 10px 0 2px",
              borderBottom: canDerive ? "1px solid #f1f5f9" : "none",
              background: "#fafbfc",
            }}
          >
            {canDerive ? (
              <div className="anim-edit__tabs">
                {TAB_IDS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`anim-edit__tab${activeTab === id ? " anim-edit__tab--active" : ""}`}
                    onClick={() => setActiveTab(id)}
                  >
                    {tabLabels[id]}
                  </button>
                ))}
              </div>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => setAdvancedOpen(false)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                border: "none",
                background: "none",
                color: "#64748b",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                padding: "0 8px",
                whiteSpace: "nowrap",
              }}
            >
              <LuChevronLeft size={12} /> {i18n.t("AnimEdit.SimpleView")}
            </button>
          </div>

          <div
            style={{
              padding: 13,
              minHeight: 132,
              display: "flex",
              flexDirection: "column",
              justifyContent: canDerive ? "flex-start" : "center",
              gap: 12,
            }}
          >
            {errorBanner}

            {!canDerive ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>
                {i18n.t("AnimEdit.NoSourceVideo")}
              </div>
            ) : (
              <>
                {activeTab === "duration" &&
                  (isGemini ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
                      >
                        {canFit && (
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => onExtend("to-target", fitTargetSec - animSec)}
                            style={tabPillBtnPrimaryStyle}
                          >
                            <LuClapperboard size={14} />{" "}
                            {i18n.t("ExtendToSceneDuration", { sec: Math.ceil(reachableSec) })}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={isBusy || capReached}
                          onClick={() => onExtend("once")}
                          style={tabPillBtnStyle}
                        >
                          <LuFilm size={13} /> {i18n.t("ExtendAnimation")}
                        </button>
                        <span style={{ flex: 1 }} />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#94a3b8",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {capReached
                            ? i18n.t("AnimEdit.MaxDurationHint")
                            : i18n.t("AnimEdit.RangeSummary", {
                                from: animSec.toFixed(1),
                                to: (animSec + EXTEND_CHUNK_SEC).toFixed(1),
                                cap: CAP_SEC,
                              })}
                        </span>
                      </div>
                      <div style={{ height: 1, background: "#f1f5f9" }} />
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
                      >
                        <span style={{ ...railHeaderStyle, marginBottom: 0 }}>
                          {i18n.t("AnimEdit.ExtendSettingsLabel")}
                        </span>
                        <span style={chipStyle}>
                          <LuImage
                            size={12}
                            style={{ color: referenceImage ? "#7c3aed" : "inherit" }}
                          />
                          {referenceImage
                            ? referenceImage.name || i18n.t("AnimEdit.TabContinuation")
                            : i18n.t("AnimEdit.NoContinuationImage")}
                        </span>
                        <span style={chipStyle}>
                          <LuFilm size={12} style={{ color: "#7c3aed" }} />
                          {extendProvider === "gemini" ? "Gemini" : "Gemini Fast"}
                        </span>
                        <span style={chipStyle}>
                          {extendPrompt
                            ? extendPrompt.length > 24
                              ? `${extendPrompt.slice(0, 24)}…`
                              : extendPrompt
                            : i18n.t("AnimEdit.NoPromptGiven")}
                        </span>
                        <button
                          type="button"
                          onClick={() => setActiveTab("continuation")}
                          style={{
                            border: "none",
                            background: "none",
                            color: "#7c3aed",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          {i18n.t("AnimEdit.Change")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      {i18n.t("AnimEdit.ExtendUnavailableProvider", {
                        provider: version.provider || "?",
                      })}
                    </div>
                  ))}

                {activeTab === "format" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={onConvert}
                      style={tabPillBtnStyle}
                      title={i18n.t(version.loop ? "ConvertToPlayOnce" : "ConvertToLoop")}
                    >
                      <LuRefreshCw size={13} />
                      {i18n.t(version.loop ? "ConvertToPlayOnce" : "ConvertToLoop")}
                    </button>
                    <button
                      type="button"
                      disabled={isBusy || mp4Applied}
                      onClick={onAddAsMp4}
                      style={tabPillBtnStyle}
                      title={i18n.t(mp4Applied ? "AnimEdit.Applied" : "AnimEdit.AddAsMp4")}
                    >
                      {mp4Applied ? (
                        <>
                          <LuCheck size={13} /> {i18n.t("AnimEdit.Applied")}
                        </>
                      ) : (
                        <>
                          <LuVideo size={13} /> {i18n.t("AnimEdit.AddAsMp4")}
                        </>
                      )}
                    </button>
                    {isVideoSourceBox && !isMp4 && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={onAddAsWebp}
                        style={tabPillBtnStyle}
                        title={i18n.t("AnimEdit.AddAsWebp")}
                      >
                        <LuImage size={13} /> {i18n.t("AnimEdit.AddAsWebp")}
                      </button>
                    )}
                  </div>
                )}

                {activeTab === "continuation" &&
                  (isGemini ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "120px minmax(0, 1fr)",
                        gap: 14,
                        alignItems: "stretch",
                      }}
                    >
                      <div
                        style={{
                          flex: "none",
                          border: "2px dashed #e2e8f0",
                          borderRadius: 10,
                          background: "#f8fafc",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          color: "#94a3b8",
                          height: 120,
                          overflow: "hidden",
                        }}
                      >
                        {referenceImage ? (
                          <img
                            src={referenceImage.url}
                            alt="reference"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              width: "auto",
                              height: "auto",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <>
                            <LuImage size={22} />
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 600,
                                textAlign: "center",
                                lineHeight: 1.35,
                                padding: "0 6px",
                              }}
                            >
                              {i18n.t("AnimEdit.NoContinuationImage")}
                            </span>
                          </>
                        )}
                      </div>
                      <div
                        style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          {referenceImage ? (
                            <button
                              type="button"
                              onClick={onClearReference}
                              disabled={isBusy}
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#dc3545",
                                background: "none",
                                border: "1px solid #fecaca",
                                borderRadius: 8,
                                padding: "7px 14px",
                                cursor: isBusy ? "not-allowed" : "pointer",
                              }}
                            >
                              {i18n.t("Remove")}
                            </button>
                          ) : (
                            <>
                              <label
                                style={{
                                  ...tabPillBtnStyle,
                                  cursor: referenceUploading ? "wait" : "pointer",
                                }}
                              >
                                {referenceUploading
                                  ? i18n.t("Uploading")
                                  : i18n.t("AnimEdit.Upload")}
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg"
                                  style={{ display: "none" }}
                                  disabled={isBusy || referenceUploading}
                                  onChange={(e) => onReferenceUpload(e.target.files?.[0])}
                                />
                              </label>
                              {!embed && (
                                <>
                                  <button
                                    type="button"
                                    onClick={onPickReferenceScene}
                                    disabled={isBusy}
                                    style={tabPillBtnStyle}
                                  >
                                    {i18n.t("AnimEdit.PickFromScene")}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={onPickReferenceLibrary}
                                    disabled={isBusy}
                                    style={tabPillBtnStyle}
                                  >
                                    {i18n.t("AnimEdit.PickFromLibrary")}
                                  </button>
                                </>
                              )}
                              {showPlayerBar && (
                                <button
                                  type="button"
                                  disabled={
                                    isBusy || frameGrabbing || referenceUploading || !playerDuration
                                  }
                                  onClick={handleUseFrame}
                                  style={tabPillBtnStyle}
                                >
                                  {frameGrabbing || referenceUploading ? (
                                    <>
                                      <LuLoaderCircle
                                        size={13}
                                        style={{ animation: "spin 1s linear infinite" }}
                                      />{" "}
                                      {i18n.t("Processing")}…
                                    </>
                                  ) : (
                                    <>
                                      <LuFilm size={13} />{" "}
                                      {i18n.t("AnimEdit.UseFrameAsContinuation")}
                                    </>
                                  )}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        {referenceError && (
                          <div style={{ color: "#dc3545", fontSize: 11 }}>{referenceError}</div>
                        )}
                        <div>
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => setPromptOpen(!promptExpanded)}
                            style={{
                              ...belowImageBtnStyle,
                              border: `1px dashed ${promptExpanded ? "#c7d2fe" : "#cbd5e1"}`,
                              background: promptExpanded ? "#eef2ff" : "transparent",
                              color: promptExpanded ? "#7c3aed" : "#64748b",
                              cursor: isBusy ? "not-allowed" : "pointer",
                              opacity: isBusy ? 0.6 : 1,
                            }}
                          >
                            <LuPlus size={12} /> {i18n.t("AnimEdit.AddPrompt")}
                          </button>
                          {promptExpanded && (
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={extendPrompt}
                              disabled={isBusy}
                              onChange={(e) => onExtendPromptChange(e.target.value)}
                              placeholder={i18n.t("AnimEdit.ContinuationPromptPlaceholder")}
                              style={{
                                marginTop: 6,
                                borderRadius: 8,
                                border: "1.5px solid #e2e8f0",
                                fontSize: 12.5,
                                resize: "vertical",
                              }}
                            />
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{ ...railHeaderStyle, marginBottom: 0, whiteSpace: "nowrap" }}
                          >
                            {i18n.t("AnimEdit.Model")}
                          </span>
                          <Form.Control
                            as="select"
                            value={extendProvider}
                            disabled={isBusy}
                            onChange={(e) => onExtendProviderChange(e.target.value)}
                            style={{
                              width: 180,
                              borderRadius: 8,
                              border: "1.5px solid #e2e8f0",
                              fontSize: 12,
                              height: 32,
                              padding: "0 10px",
                            }}
                          >
                            <option value="gemini-fast">Gemini Fast</option>
                            <option value="gemini">Gemini</option>
                          </Form.Control>
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>
                            {i18n.t("AnimEdit.ModelGeminiOnlyHint")}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      {i18n.t("AnimEdit.ExtendUnavailableProvider", {
                        provider: version.provider || "?",
                      })}
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
